import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  getSession,
  redirectToLogin,
  redirectToAddAccount,
  switchToAccount as switchToAccountRedirect,
  switchOrganization as switchOrgRedirect,
  logout as logoutAction,
  clearPlatformCsrfToken,
} from '../lib/auth';
import type { SessionResponse } from '../lib/auth';
// Contract types come from the shared SDK. `SessionResponse` is the app-local
// normalized view getSession() returns; the account/org/linked/auth shapes
// inside it are the frozen SDK types.
import type {
  SessionOrganization,
  LinkedAccount,
  SessionAuthStatus,
} from '@mayline-ai/platform-auth';
import { setOnUnauthorized } from '../lib/auth-events';
import {
  clearDevIdentityOverrides,
  getDevSession,
  isDevModeEnabled,
  setDevOrganizationOverride,
} from '../lib/dev-auth';

/**
 * User information from session claims.
 */
export interface AuthUser {
  id: string;
  name: string;
  email: string;
  organizationId: string;
  role: 'Viewer' | 'Member' | 'Admin' | 'Owner';
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  isAdmin: boolean;
  /**
   * Whether the signed-in user is a Parslee staffer. Gates the entire admin
   * dashboard via StaffGate. Fail-closed (false when the signal is absent).
   */
  isParsleeStaff: boolean;
  organizations: SessionOrganization[];
  linkedAccounts: LinkedAccount[];
  authStatus: SessionAuthStatus | null;
  hasOrganization: boolean;
  entitledProducts: string[];
  login: (returnUrl?: string) => void;
  logout: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  addAccount: () => void;
  switchToAccount: (email: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY_USER_ID = 'admindashboard_session_user_id';
const STORAGE_KEY_ORG_ID = 'admindashboard_organization_id';

/**
 * Session-based auth provider.
 * Validates session via /connect/session (cookie-based, same-origin proxy).
 * Login redirects to /connect/login (OAuth via m365dotnet).
 *
 * Supports linked accounts, org switching, cross-tab sync, and identity
 * mismatch detection following the SalesTrainer / Studio auth-provider pattern.
 */
export function ParsleeAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [organizations, setOrganizations] = useState<SessionOrganization[]>([]);
  const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
  const [authStatus, setAuthStatus] = useState<SessionAuthStatus | null>(null);
  const [entitledProducts, setEntitledProducts] = useState<string[]>([]);
  const [isParsleeStaff, setIsParsleeStaff] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionRequestIdRef = useRef(0);

  const clearAuthState = useCallback(() => {
    setUser(null);
    setOrganizations([]);
    setLinkedAccounts([]);
    setEntitledProducts([]);
    setAuthStatus(null);
    setIsParsleeStaff(false);
    clearPlatformCsrfToken();
  }, []);

  /** Apply a successful session response into component state. */
  const applySessionData = useCallback((data: SessionResponse, requestId: number) => {
    // Stale-request guard: ignore responses from superseded fetches
    if (requestId !== sessionRequestIdRef.current) {
      return;
    }

    if (data.authenticated && data.account) {
      // Identity mismatch detection: if the session user changed out from under
      // us (e.g. another tab switched accounts), clear stale org state.
      try {
        const storedUserId = localStorage.getItem(STORAGE_KEY_USER_ID);
        if (storedUserId && storedUserId !== data.account.id) {
          localStorage.removeItem(STORAGE_KEY_ORG_ID);
        }
        localStorage.setItem(STORAGE_KEY_USER_ID, data.account.id);
        if (data.activeOrganization) {
          localStorage.setItem(STORAGE_KEY_ORG_ID, data.activeOrganization);
        }
      } catch {
        // localStorage may be unavailable (private browsing, quota)
      }

      setUser({
        id: data.account.id,
        name: data.account.name,
        email: data.account.email,
        organizationId: data.activeOrganization ?? '',
        role: (data.role as AuthUser['role']) ?? 'Member',
      });
      setOrganizations(data.organizations ?? []);
      setLinkedAccounts(data.linkedAccounts ?? []);
      setEntitledProducts(data.entitledProducts ?? []);
      setAuthStatus(data.authStatus ?? null);
      setIsParsleeStaff(data.isParsleeStaff === true);
    } else {
      clearAuthState();
    }
  }, [clearAuthState]);

  const fetchSession = useCallback(async () => {
    const requestId = ++sessionRequestIdRef.current;

    if (isDevModeEnabled()) {
      applySessionData(getDevSession(), requestId);
      setIsLoading(false);
      return;
    }

    try {
      const data = await getSession();
      applySessionData(data, requestId);
    } catch {
      if (requestId === sessionRequestIdRef.current) {
        clearAuthState();
      }
    } finally {
      if (requestId === sessionRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [applySessionData, clearAuthState]);

  // Wire 401 handling: clear auth state when any API call gets a 401
  useEffect(() => {
    setOnUnauthorized(() => {
      clearAuthState();
    });
    return () => setOnUnauthorized(null);
  }, [clearAuthState]);

  // Fetch session on mount
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Re-fetch on window focus, visibility change, and cross-tab storage events
  useEffect(() => {
    const debouncedRefetch = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        fetchSession();
      }, 1000);
    };

    const handleFocus = () => debouncedRefetch();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        debouncedRefetch();
      }
    };

    // Cross-tab sync: 'storage' fires in other tabs when localStorage changes.
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_USER_ID || e.key === STORAGE_KEY_ORG_ID) {
        fetchSession();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [fetchSession]);

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'Admin' || user?.role === 'Owner';
  const hasOrganization = !!user?.organizationId;

  const login = useCallback((returnUrl?: string) => {
    if (isDevModeEnabled()) {
      void fetchSession();
      return;
    }

    redirectToLogin(returnUrl);
  }, [fetchSession]);

  const logout = useCallback(async () => {
    // Clear state before redirect since logoutAction navigates away
    clearAuthState();
    try {
      localStorage.removeItem(STORAGE_KEY_USER_ID);
      localStorage.removeItem(STORAGE_KEY_ORG_ID);
    } catch { /* ignore */ }

    if (isDevModeEnabled()) {
      clearDevIdentityOverrides();
      setIsLoading(false);
      return;
    }

    await logoutAction();
  }, [clearAuthState]);

  const switchOrg = useCallback(async (orgId: string) => {
    // Same-org guard: re-picking the org you're already on is a no-op.
    if (user?.organizationId && orgId === user.organizationId) {
      return;
    }

    if (isDevModeEnabled()) {
      setDevOrganizationOverride(orgId);
      await fetchSession();
      return;
    }

    try { localStorage.setItem(STORAGE_KEY_ORG_ID, orgId); } catch { /* ignore */ }
    try {
      await switchOrgRedirect(orgId);
    } catch (error) {
      await fetchSession();
      throw error;
    }
  }, [fetchSession, user?.organizationId]);

  const addAccount = useCallback(() => {
    redirectToAddAccount();
  }, []);

  const switchToAccount = useCallback((email: string) => {
    try {
      localStorage.removeItem(STORAGE_KEY_ORG_ID);
      localStorage.removeItem(STORAGE_KEY_USER_ID);
    } catch { /* ignore */ }
    // Prefer the linked account's accountId (m365's /connect/switch-session does
    // an O(1) membership check on accountId). Resolve it case-insensitively from
    // the in-context linkedAccounts; fall back to the email when there's no match.
    const normalized = email.trim().toLowerCase();
    const match = linkedAccounts.find(
      (a) => a.email.trim().toLowerCase() === normalized,
    );
    switchToAccountRedirect(match?.accountId || email);
  }, [linkedAccounts]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      isAdmin,
      isParsleeStaff,
      organizations,
      linkedAccounts,
      authStatus,
      hasOrganization,
      entitledProducts,
      login,
      logout,
      switchOrg,
      addAccount,
      switchToAccount,
    }),
    [isAuthenticated, isLoading, user, isAdmin, isParsleeStaff, organizations, linkedAccounts, authStatus, hasOrganization, entitledProducts, login, logout, switchOrg, addAccount, switchToAccount]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within a ParsleeAuthProvider");
  }
  return context;
}
